/**
 * PublishedContractFormDialog
 * Dialog de criação/edição de um Contrato Publicado
 * 
 * 4 abas:
 * - Geral: nome, descrição, status, template, versão, workspaces
 * - Provider: provider de assinatura, métodos de entrega, selfie
 * - Signatários: ordem e método padrão por papel
 * - Preview: visualização do template selecionado
 * 
 * RELEASE: v1.0.0 - 2026-02-19
 */

import React, { useState, useEffect } from 'react';
import { Loader2, Info, AlertTriangle, FileSignature, Plus } from 'lucide-react';
import { ContractTemplateFormDialog } from '@/components/contracts/ContractTemplateFormDialog';
import { usePermissions } from '@/hooks/usePermissions';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { ContractTemplatePreview } from '@/components/contracts/ContractTemplatePreview';
import { PublishedContractSignersConfig } from './PublishedContractSignersConfig';
import { PublishedContractDocsConfig } from './PublishedContractDocsConfig';
import { useContractTemplates } from '@/hooks/useContractTemplates';
import { useContractTemplateVersions } from '@/hooks/useContractTemplateVersions';
import { useTenantSignatureConfigs } from '@/hooks/useTenantSignatureConfigs';
import { useCrmWorkspacesHierarchy } from '@/hooks/useCrmWorkspacesHierarchy';
import {
  PUBLISHED_CONTRACT_STATUS_LABELS,
  type PublishedContract,
  type PublishedContractInput,
  type DefaultSigner,
  type ContractTemplate,
  type ContractTemplateVersion,
  type ContractTemplateInput,
} from '@/types/contracts';

// Props do dialog
interface PublishedContractFormDialogProps {
  open: boolean;
  onClose: () => void;
  /** Contrato para edição (null = criação) */
  contract?: PublishedContract | null;
  onSave: (input: PublishedContractInput & { id?: string }) => void;
  isSaving?: boolean;
  /** Indica se o tenant possui o módulo contrato digital */
  hasDigitalContractsModule?: boolean;
  /** Quantidade atual de contratos em produção */
  productionCount?: number;
}

/**
 * Dialog de criação/edição de contratos publicados
 */
export function PublishedContractFormDialog({
  open,
  onClose,
  contract,
  onSave,
  isSaving = false,
  hasDigitalContractsModule = false,
  productionCount = 0,
}: PublishedContractFormDialogProps) {
  const isEditing = !!contract;

  // Dados do formulário
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [status, setStatus] = useState<'draft' | 'production' | 'inactive'>('draft');
  const [templateId, setTemplateId] = useState('');
  const [templateVersionId, setTemplateVersionId] = useState<string | null>(null);
  const [signatureConfigId, setSignatureConfigId] = useState<string | null>(null);
  const [allowedMethods, setAllowedMethods] = useState<('email' | 'whatsapp' | 'sms')[]>(['email']);
  const [requireSelfie, setRequireSelfie] = useState(false);
  const [workspaceIds, setWorkspaceIds] = useState<string[]>([]);
  const [defaultSigners, setDefaultSigners] = useState<DefaultSigner[]>([]);

  // Hooks de dados
  const { templates, isLoading: loadingTemplates, createTemplate } = useContractTemplates();

  // Permissão para criar novo modelo de contrato (atalho "+" ao lado do select)
  const { canCreate } = usePermissions();
  const canCreateTemplate = canCreate('crm_contract_templates');

  // Estado do modal de novo modelo (atalho)
  const [templateFormOpen, setTemplateFormOpen] = useState(false);
  const { data: versions = [], isLoading: loadingVersions } = useContractTemplateVersions(templateId || undefined);
  const { configs: signatureConfigs, isLoading: loadingConfigs } = useTenantSignatureConfigs();
  const { workspaces, isLoading: loadingWorkspaces } = useCrmWorkspacesHierarchy();

  // Template selecionado (para preview)
  const selectedTemplate = templates.find((t) => t.id === templateId);

  // Versão selecionada (para preview)
  const selectedVersion = versions.find((v) => v.id === templateVersionId);

  // Provider selecionado
  const selectedConfig = signatureConfigs?.find((c) => c.id === signatureConfigId);
  const providerSlug = selectedConfig?.signature_providers?.slug;
  const isAutentique = providerSlug === 'autentique';

  // Popula formulário ao editar
  useEffect(() => {
    if (open && contract) {
      setName(contract.name);
      setDescription(contract.description || '');
      setStatus(contract.status);
      setTemplateId(contract.template_id);
      setTemplateVersionId(contract.template_version_id);
      setSignatureConfigId(contract.signature_config_id);
      setAllowedMethods(contract.allowed_delivery_methods as ('email' | 'whatsapp' | 'sms')[]);
      setRequireSelfie(contract.require_selfie);
      setWorkspaceIds(contract.workspace_ids || []);
      setDefaultSigners(contract.default_signers || []);
    } else if (open && !contract) {
      // Reset para criação
      setName('');
      setDescription('');
      setStatus('draft');
      setTemplateId('');
      setTemplateVersionId(null);
      setSignatureConfigId(null);
      setAllowedMethods(['email']);
      setRequireSelfie(false);
      setWorkspaceIds([]);
      setDefaultSigners([]);
    }
  }, [open, contract]);

  // Ao trocar o template, limpa versão selecionada
  const handleTemplateChange = (id: string) => {
    setTemplateId(id);
    setTemplateVersionId(null);
  };

  // Ao trocar o provider, ajusta métodos de entrega
  const handleProviderChange = (id: string) => {
    setSignatureConfigId(id || null);
    // Reset dos métodos inválidos
    if (!id) {
      setAllowedMethods(['email']);
      setRequireSelfie(false);
    }
  };

  // Toggle de workspace selecionado
  const handleWorkspaceToggle = (wsId: string) => {
    setWorkspaceIds((prev) =>
      prev.includes(wsId) ? prev.filter((id) => id !== wsId) : [...prev, wsId]
    );
  };

  // Toggle de método de entrega
  const handleMethodToggle = (method: 'email' | 'whatsapp' | 'sms') => {
    setAllowedMethods((prev) => {
      if (prev.includes(method)) {
        // Garante ao menos um método
        if (prev.length === 1) return prev;
        return prev.filter((m) => m !== method);
      }
      return [...prev, method];
    });
  };

  // Conteúdo HTML para o preview (usa versão fixada ou template atual)
  const previewHtml = selectedVersion?.content_html || selectedTemplate?.content_html || '';

  // Verifica se status production está bloqueado pelo limite
  // Permite se: tem módulo, OU não tem nenhum em produção, OU está editando um que JÁ está em produção
  const isProductionBlocked = 
    !hasDigitalContractsModule && 
    status === 'production' && 
    productionCount >= 1 && 
    !(contract?.status === 'production'); // Se já está em produção, permite manter

  // Submete o formulário
  const handleSubmit = () => {
    if (!name.trim() || !templateId) return;

    // Bloqueia se tentar colocar em produção sem módulo e já tem 1
    if (isProductionBlocked) {
      return; // Não permite submit — UI já mostra aviso
    }

    onSave({
      id: contract?.id,
      name: name.trim(),
      description: description.trim() || undefined,
      template_id: templateId,
      template_version_id: templateVersionId,
      signature_config_id: signatureConfigId || undefined,
      status,
      allowed_delivery_methods: allowedMethods,
      require_selfie: requireSelfie,
      workspace_ids: workspaceIds,
      default_signers: defaultSigners,
    });
  };

  const canSubmit = name.trim() && templateId && !isProductionBlocked;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col p-0">
        {/* Header */}
        <DialogHeader className="px-6 pt-6 pb-0">
          <DialogTitle className="flex items-center gap-2 text-base">
            <FileSignature className="h-4 w-4 text-primary" />
            {isEditing ? 'Editar Contrato' : 'Novo Contrato'}
          </DialogTitle>
        </DialogHeader>

        {/* Abas */}
        <Tabs defaultValue="geral" className="flex-1 overflow-hidden flex flex-col">
           <TabsList className="mx-6 mt-4 shrink-0 grid grid-cols-5">
             <TabsTrigger value="geral">Geral</TabsTrigger>
             <TabsTrigger value="provider">Provider</TabsTrigger>
             <TabsTrigger value="signatarios">Signatários</TabsTrigger>
             <TabsTrigger value="documentos">Documentos</TabsTrigger>
             <TabsTrigger value="preview" disabled={!previewHtml}>Preview</TabsTrigger>
           </TabsList>

          {/* ============================================================
              ABA 1 — GERAL
              ============================================================ */}
          <TabsContent value="geral" className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
            {/* Nome */}
            <div className="space-y-1.5">
              <Label>Nome do Contrato <span className="text-destructive">*</span></Label>
              <Input
                placeholder="Ex: Contrato de Venda de Consórcio"
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={200}
              />
            </div>

            {/* Descrição */}
            <div className="space-y-1.5">
              <Label>Descrição</Label>
              <Textarea
                placeholder="Breve descrição do contrato..."
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={2}
                maxLength={500}
              />
            </div>

            {/* Status */}
            <div className="space-y-1.5">
              <Label>Status</Label>
              <Select value={status} onValueChange={(v) => setStatus(v as typeof status)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(Object.entries(PUBLISHED_CONTRACT_STATUS_LABELS) as [string, { label: string }][]).map(([value, { label }]) => (
                    <SelectItem key={value} value={value}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Apenas contratos com status <strong>Produção</strong> aparecem no wizard de geração
              </p>
              {/* Alerta de limite de produção */}
              {isProductionBlocked && (
                <Alert variant="destructive" className="mt-2">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription className="text-xs">
                    Limite atingido: já existe 1 contrato em produção. Para ativar mais contratos,
                    adquira o <strong>Módulo Contrato Digital</strong>.
                  </AlertDescription>
                </Alert>
              )}
            </div>

            {/* Template */}
            <div className="space-y-1.5">
              <Label>Modelo de Contrato <span className="text-destructive">*</span></Label>
              {loadingTemplates ? (
                <Skeleton className="h-10 w-full rounded-md" />
              ) : (
                <div className="flex items-center gap-2">
                  <div className="flex-1 min-w-0">
                    <Select value={templateId} onValueChange={handleTemplateChange}>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione um modelo..." />
                      </SelectTrigger>
                      <SelectContent>
                        {templates.filter((t) => t.is_active).map((t) => (
                          <SelectItem key={t.id} value={t.id}>
                            <div className="flex items-center gap-2">
                              <span>{t.name}</span>
                              <Badge variant="outline" className="text-[10px] h-4">{t.category}</Badge>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {/* Atalho: abre modal de criação de novo modelo de contrato */}
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    className="h-10 w-10 shrink-0"
                    disabled={!canCreateTemplate}
                    title={
                      canCreateTemplate
                        ? 'Cadastrar novo modelo de contrato'
                        : 'Você não tem permissão para criar modelos de contrato'
                    }
                    onClick={() => setTemplateFormOpen(true)}
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </div>

            {/* Versão do template */}
            {templateId && (
              <div className="space-y-1.5">
                <Label>Versão do Modelo</Label>
                {loadingVersions ? (
                  <Skeleton className="h-10 w-full rounded-md" />
                ) : (
                  <Select
                    value={templateVersionId || 'latest'}
                    onValueChange={(v) => setTemplateVersionId(v === 'latest' ? null : v)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="latest">Sempre a versão mais recente</SelectItem>
                      {versions.map((v) => (
                        <SelectItem key={v.id} value={v.id}>
                          v1.{v.version_number - 1} — {new Date(v.created_at).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })}
                          {v.change_note ? ` (${v.change_note})` : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                <p className="text-xs text-muted-foreground">
                  Fixe uma versão específica para garantir que o contrato não mude ao editar o modelo
                </p>
              </div>
            )}

            {/* Workspaces */}
            <div className="space-y-1.5">
              <Label>Workspaces Vinculados</Label>
              <p className="text-xs text-muted-foreground">
                Deixe sem selecionar para aparecer em todos os workspaces
              </p>
              {loadingWorkspaces ? (
                <Skeleton className="h-10 w-full rounded-md" />
              ) : (
                <div className="flex flex-wrap gap-2 mt-1">
                  {workspaces.map((ws) => {
                    const selected = workspaceIds.includes(ws.id);
                    return (
                      <button
                        key={ws.id}
                        type="button"
                        onClick={() => handleWorkspaceToggle(ws.id)}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs transition-colors ${
                          selected
                            ? 'border-primary bg-primary/10 text-primary'
                            : 'border-border hover:border-muted-foreground'
                        }`}
                      >
                        {ws.color && (
                          <span
                            className="w-2.5 h-2.5 rounded-full shrink-0"
                            style={{ backgroundColor: ws.color }}
                          />
                        )}
                        {ws.name}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </TabsContent>

          {/* ============================================================
              ABA 2 — PROVIDER DE ASSINATURA
              ============================================================ */}
          <TabsContent value="provider" className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
            {/* Select do provider */}
            <div className="space-y-1.5">
              <Label>Provider de Assinatura Digital</Label>
              {loadingConfigs ? (
                <Skeleton className="h-10 w-full rounded-md" />
              ) : (
                <Select
                  value={signatureConfigId || 'none'}
                  onValueChange={(v) => handleProviderChange(v === 'none' ? '' : v)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione um provider..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sem provider (configurar depois)</SelectItem>
                    {(signatureConfigs || []).filter((c) => c.is_active).map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        <div className="flex items-center gap-2">
                          <span>{c.signature_providers?.display_name || c.signature_providers?.name}</span>
                          {c.is_default && (
                            <Badge variant="outline" className="text-[10px] h-4">Padrão</Badge>
                          )}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              {!signatureConfigId && (
                <Alert className="mt-2">
                  <Info className="h-4 w-4" />
                  <AlertDescription className="text-xs">
                    Configure os providers de assinatura em Configurações → Assinatura Digital
                  </AlertDescription>
                </Alert>
              )}
            </div>

            {/* Métodos de entrega — somente se provider selecionado */}
            {signatureConfigId && (
              <>
                <div className="space-y-2">
                  <Label>Métodos de Entrega Permitidos</Label>
                  <p className="text-xs text-muted-foreground">
                    Como o link de assinatura será enviado ao signatário
                  </p>
                  <div className="flex gap-4 mt-1">
                    {(['email', 'whatsapp', 'sms'] as const).map((method) => {
                      // SMS apenas para providers que suportam
                      if (method === 'sms' && !isAutentique) return null;
                      return (
                        <label key={method} className="flex items-center gap-2 cursor-pointer">
                          <Checkbox
                            checked={allowedMethods.includes(method)}
                            onCheckedChange={() => handleMethodToggle(method)}
                          />
                          <span className="text-sm capitalize">
                            {method === 'whatsapp' ? 'WhatsApp' : method === 'email' ? 'E-mail' : 'SMS'}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </div>

                {/* Exigir selfie — somente Autentique */}
                {isAutentique && (
                  <div className="space-y-2">
                    <Label>Verificação Adicional</Label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <Checkbox
                        checked={requireSelfie}
                        onCheckedChange={(v) => setRequireSelfie(!!v)}
                      />
                      <div>
                        <span className="text-sm font-medium">Exigir selfie do signatário</span>
                        <p className="text-xs text-muted-foreground">
                          Ativa liveness check (LIVE) no Autentique para cada signatário
                        </p>
                      </div>
                    </label>
                  </div>
                )}
              </>
            )}
          </TabsContent>

          {/* ============================================================
              ABA 3 — SIGNATÁRIOS PADRÃO
              ============================================================ */}
          <TabsContent value="signatarios" className="flex-1 overflow-y-auto px-6 py-4">
            <PublishedContractSignersConfig
              signers={defaultSigners}
              onChange={setDefaultSigners}
              allowedMethods={allowedMethods}
            />
          </TabsContent>

          {/* ============================================================
              ABA 4 — DOCUMENTOS EXIGIDOS
              ============================================================ */}
          <TabsContent value="documentos" className="flex-1 overflow-y-auto px-6 py-4">
            <PublishedContractDocsConfig
              publishedContractId={contract?.id}
            />
          </TabsContent>

          {/* ============================================================
              ABA 5 — PREVIEW
              ============================================================ */}
          <TabsContent value="preview" className="flex-1 overflow-y-auto px-6 py-4">
            {previewHtml ? (
              <ContractTemplatePreview
                contentHtml={previewHtml}
              />
            ) : (
              <div className="py-12 text-center text-muted-foreground text-sm">
                Selecione um modelo na aba Geral para visualizar o preview
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Footer */}
        <DialogFooter className="px-6 py-4 border-t shrink-0 flex gap-2 justify-end">
          <Button variant="outline" onClick={onClose} disabled={isSaving}>
            Cancelar
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit || isSaving}>
            {isSaving ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Salvando...
              </>
            ) : isEditing ? 'Salvar Alterações' : 'Criar Contrato'}
          </Button>
        </DialogFooter>
      </DialogContent>

      {/* Modal de atalho: criação de novo modelo de contrato.
          Ao salvar, o hook useContractTemplates invalida a query e o novo
          item aparece automaticamente no Select acima. */}
      <ContractTemplateFormDialog
        open={templateFormOpen}
        onClose={() => setTemplateFormOpen(false)}
        template={null}
        onSave={(input: ContractTemplateInput & { id?: string }) => {
          createTemplate.mutate(input as ContractTemplateInput, {
            onSuccess: () => setTemplateFormOpen(false),
          });
        }}
        isSaving={createTemplate.isPending}
      />
    </Dialog>
  );
}
